import { JSONContent } from '@tiptap/react';
import { flowEditorState } from '../flowEditorState';
import type {
  BlockOption,
  NodeData as SharedNodeData,
} from '@duke-hq/svc-api/student/trpc/routers/resourceEnrolments/helpers/flowContentSchemas';

export type { BlockOption };

// Tiptap node types that are embedded media, not text. A bubble holding only one
// of these is not "empty", and a change to one is a media edit not a text edit.
// `upload-component` is the in-progress placeholder a file/image becomes before it
// patches to its final node, so it counts too.
export const MEDIA_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'resizableMedia',
  'fileComponent',
  'upload-component',
]);

// `personaId` lets append paths stamp the new bubble with the persona it should
// inherit (usually the previous message's). Callers creating the first bubble of
// a fresh node omit it and fall back to the editor-wide default persona.
export const chatMessageDBlock = (
  item: JSONContent,
  personaId: string | null = flowEditorState.defaultPersonaId,
): JSONContent => {
  const wrapper: JSONContent = {
    type: 'dBlock',
    attrs: {
      personaId,
    },
    content: [item],
  };

  return wrapper;
};

// The persona a newly appended bubble should inherit: the last existing bubble's
// persona, falling back to the editor-wide default when there is nothing to carry
// over (e.g. the node only holds the empty starter paragraph). Centralised so
// every append path (Enter, paste, action menu, link) carries persona the same
// way, rather than each reimplementing it (or, as the Enter path did, forgetting).
export const inheritedPersonaId = (root: JSONContent): string | null => {
  const lastBlock = root.content?.[root.content.length - 1];
  const personaId = lastBlock?.attrs?.personaId;

  return typeof personaId === 'string' ? personaId : flowEditorState.defaultPersonaId;
};

// A top-level message bubble (dBlock) is "empty" when it carries no text and no
// embedded media, just an empty placeholder paragraph. Pressing Enter creates one
// before the author types into it.
export const isEmptyMessageBubble = (block: JSONContent): boolean => {
  if (block.type !== 'dBlock') {
    return false;
  }
  let hasContent = false;
  const walk = (node: JSONContent) => {
    if (typeof node.text === 'string' && node.text.length > 0) {
      hasContent = true;
    }
    if (node.type && MEDIA_BLOCK_TYPES.has(node.type)) {
      hasContent = true;
    }
    node.content?.forEach(walk);
  };
  walk(block);
  return !hasContent;
};

// Whether the doc has any bubble. Unlike `editor.isEmpty`, an empty placeholder counts.
export const hasMessageBubbles = (root: JSONContent): boolean =>
  Boolean(root.content?.some((block) => block.type === 'dBlock'));

// Append `newBlock` the way the Enter handler does: an empty doc (an options-only
// node serialises with no `content`) becomes `[newBlock]`; otherwise append with
// the last bubble's persona. A trailing empty placeholder absorbs the append:
// another empty block is a no-op (no double-Enter dupes), a substantive one
// replaces it. Pure. Shared by the Enter path and the recorder tests.
export const appendMessageBlock = (
  root: JSONContent,
  newBlock: JSONContent,
  isEmpty: boolean,
): JSONContent => {
  if (isEmpty || !root.content?.length) {
    return { ...root, content: [newBlock] };
  }
  const lastBlock = root.content[root.content.length - 1];
  const lastIsEmptyPlaceholder = lastBlock ? isEmptyMessageBubble(lastBlock) : false;
  if (lastIsEmptyPlaceholder && isEmptyMessageBubble(newBlock)) {
    return root;
  }
  const personaId = inheritedPersonaId(root);
  const stamped = personaId ? { ...newBlock, attrs: { ...newBlock.attrs, personaId } } : newBlock;
  const kept = lastIsEmptyPlaceholder ? root.content.slice(0, -1) : root.content;
  return { ...root, content: [...kept, stamped] };
};

// `tiptap` is widened from the shared schema's strict 'doc'-rooted shape to
// the generic `JSONContent` type because admin's editor write paths assign
// the result of `editor.getJSON()` directly. The shared schema is correct
// for what's persisted; admin is correct for what flows through state.
export type NodeData = Omit<SharedNodeData, 'tiptap'> & {
  tiptap?: JSONContent;
};
